/**
 * renderDiagnosticsBus.js
 *
 * 전역 렌더 진단 버스 — "화면이 까맣게 안 그려지는" 원인을 어디서든(훅·월드·fetch catch)
 * 배너로 흘려보내는 단일 통로.
 *
 * 왜 필요한가:
 *   렌더 실패 신호가 두 군데서 난다.
 *     (1) GameCanvas 내부 — 워치독(시간 정체) / webglcontextlost / 셰이더 링크 실패
 *     (2) GameCanvas '바깥' — RpgWorld·훅의 데이터 fetch 가 catch(_){} 로 조용히 실패
 *   (2)는 배너에 닿을 길이 없어 "노란 정체 배너"까지만 떴다(원인 불명). 이 버스로 (1)(2)를
 *   한 곳에 모아 GameEntry 가 구독 → 배너가 '진짜 원인'을 이름으로 표시한다.
 *
 * 우선순위(severity): 약한 신호가 이미 잡힌 구체적 원인을 덮어쓰지 못하게 한다.
 *   loading-slow(1) < stalled(2) < data-error/asset-error(3) < shader-error(4) < context-lost(5)
 *   예) 파티션 fetch 실패(data-error 3)가 먼저 잡히면, 15초 뒤 워치독의 stalled(2)가 와도
 *       "지형이 안 그려짐"(막연) 대신 "지형 데이터 로드 실패"(구체)가 유지된다.
 */

const SEVERITY = {
  'loading-slow': 1,
  stalled: 2,
  'data-error': 3,
  'asset-error': 3,
  'post-black': 3,   // 'ok'(삼각형 그려짐)로 걷히면 안 된다 — 씬은 늘 그려지는 상태에서 나는 실패다
  'shader-error': 4,
  'context-lost': 5,
};

let current = null;   // 현재 표시 중인 진단(가장 심각한 것)
let listener = null;  // GameEntry 의 setRenderStatus

const sev = (s) => (s && SEVERITY[s.phase]) || 0;

/**
 * 진단 보고. 더 약한 신호는 무시(구체적 원인 우선). phase:'ok' 는 회복 신호.
 */
export function reportRenderDiagnostic(status) {
  if (!status || !status.phase) return;

  if (status.phase === 'ok') {
    // 정상 렌더 재개 — 배너 해제. 단 확정 실패(data/asset/shader/context, severity>=3)는
    // 삼각형이 조금 그려졌다고 해제하지 않는다(플레이어만 떠도 300 넘을 수 있어 오해제 방지).
    if (current && sev(current) >= 3) return;
    current = null;
    listener?.({ phase: 'ok' });
    return;
  }

  // 컨텍스트 복구 — 'ok'(삼각형이 그려졌다)와 달리 **원인이 사라졌다**는 확정 신호라
  // severity 게이트를 통과시킨다. 종전엔 severity 0 이라 context-lost(5)에 막혀
  // 배너가 영영 안 걷혔다.
  if (status.phase === 'context-restored') {
    if (current?.phase === 'context-lost') { current = null; listener?.({ phase: 'ok' }); }
    import('./clientLogReporter.js')
      .then((m) => m.reportEventNow?.('render-diagnostic', `context-restored: ${status.detail || ''}`))
      .catch(() => {});
    return;
  }

  if (current && sev(status) < sev(current)) return; // 약한 신호는 버림
  current = status;
  listener?.(status);

  // 렌더 실패는 console.error 없이도 서버 리포트(모바일 원격 디버깅, 2026-07-15).
  //   stalled(2) 이상만 — loading-slow 는 정상 로딩에서도 뜨는 노이즈라 제외.
  if (sev(status) >= 2) {
    import('./clientLogReporter.js')
      .then((m) => m.reportEventNow?.('render-diagnostic', `${status.phase}: ${status.detail || ''}`))
      .catch(() => {});
  }
}

/**
 * 명시적 해제 — 실패했던 로드가 재시도로 성공했을 때 그 원인만 걷어낸다(transient 자가치유).
 * phase 를 주면 현재 배너가 그 원인일 때만 해제(다른 원인은 유지).
 */
export function clearRenderDiagnostic(phase) {
  if (!current) return;
  if (phase && current.phase !== phase) return;
  current = null;
  listener?.({ phase: 'ok' });
}

/** GameEntry 가 1회 구독. 구독 시점에 이미 잡힌 원인이 있으면 즉시 전달. */
export function subscribeRenderDiagnostic(fn) {
  listener = fn;
  if (current) fn(current);
  return () => { if (listener === fn) listener = null; };
}
